/**
 * @bos/portal3 — THE BUILD HALF'S TWO ROOMS: Code and Screens.
 *
 * These are the rooms the owner asked for ("do we have a path where we show generated code
 * per module?", "do we have any place to work with the screens?"). Until this batch they
 * were honest placeholders: no read surface existed on the server, and the product's rule
 * is SHOW WHAT EXISTS, NEVER FAKE WHAT DOESN'T, so they said so and pointed at the
 * download. Both surfaces exist now (`runs.codeModules` / `runs.codeFile` / `runs.screens`)
 * and the rooms show the real thing.
 *
 * ── CODE: A READ SURFACE FOR AN OWNER, NOT A REPO DUMP ─────────────────────────────────
 * The files are self-describing — a `// GENERATED by …` mark naming the behavior, a marker
 * separating the deterministic frame from the functions the machine wrote, and a `// RULE:`
 * on each of those naming the business rule it enforces. The server parses exactly those
 * marks; this room renders them as a header a person can read ("This file is 'Close folio'
 * … the two marked functions were written by the machine to enforce your rules"), tints the
 * machine-written ranges in the viewer and marks them ⚙ in the gutter. Nothing is inferred
 * beyond what the bytes say: a file that declares no such functions gets no claim made
 * about it, and there is no syntax highlighter — a heavy dependency to colour someone
 * else's code was not worth the bundle.
 *
 * NO EDITING, ON PURPOSE. An owner's hand-typed line cannot re-enter the machine's oracle
 * as anything but new unverified code, so an in-portal editor that kept the green checkmark
 * would be a lie. The honest fork is the one already shipped and now said out loud on this
 * page: take the app, it is yours.
 *
 * ── SCREENS: WHAT EXISTS, HOW IT WAS CHECKED — AND NOW, THE PICTURES ───────────────────
 * The gallery is the generated screen spec joined with the machine's own render check —
 * name, shape, who sees it, who it is hidden from, whether it came up clean.
 *
 * It used to end there, saying in words that it had no pictures, because a drawing made on
 * this page would have been this page's guess at someone else's app. That reason is gone:
 * the compile gate's render check ALREADY draws every screen to grade it, and the run now
 * keeps those drawings (`screens-preview/`, written by `@bos/compiler`). So the picture here
 * is not this page's guess — it is the same render that produced the verdict beside it.
 *
 * THREE RULES HOLD IT HONEST.
 *   1. The label above every frame is verbatim and non-negotiable: these are drawings made
 *      with rehearsal data — no rows, blank forms, real empty-state sentences — not the app.
 *   2. The frame is `sandbox=""`: no scripts, no navigation, no forms, no same-origin. The
 *      fragments contain no scripts, and the empty sandbox makes that a PROPERTY of the
 *      frame rather than a promise about its contents.
 *   3. A run WITHOUT drawings keeps the old paragraph, word for word. The pictures are an
 *      addition where they exist, never a fallback that implies one where they do not.
 *
 * REFRESH SURVIVES: the open file rides the URL (`?file=gen/folio/h-close-folio.ts`), so a
 * reload lands back on the same file — and the path is one the LISTING handed out, which
 * the server re-jails regardless (it trusts nothing this page sends).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PATHS } from '../../routes';
import { useRunRoom } from '../roomContext';
import {
  useCodeFile,
  useRunCode,
  useRunScreens,
  useScreenPreview,
  type CodeFileEntry,
  type CodeFileRead,
  type CodeModuleEntry,
  type ScreenDoc,
  type ScreenPreviewRead,
  type ScreensDoc,
  type ScreensPreviewsDoc,
} from '../hooks';

/** The one sentence both rooms end on: the machine's verdict is about the build it graded. */
function TakeItAwayLine({ runId }: { runId: string }) {
  return (
    <p className="sr__lede">
      Want to change it with your own hands? <Link to={PATHS.run(runId)}>Take the app</Link> — it
      is yours. The machine’s verdict stays true of the build it graded, not of your edits.
    </p>
  );
}

// ═════════════════════════════════ CODE ══════════════════════════════════════

/** Bytes in words, at the grain a person cares about. */
function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function stampLabel(iso: string | null): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const COUNT_WORD = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

function countWord(n: number): string {
  return n >= 0 && n < COUNT_WORD.length ? COUNT_WORD[n]! : String(n);
}

/** The file a person most likely came to see: the first behavior, else the first file. */
function defaultPath(modules: readonly CodeModuleEntry[]): string | null {
  for (const module of modules) {
    const behavior = module.files.find((f) => f.kind === 'behavior');
    if (behavior !== undefined) return behavior.path;
  }
  return modules[0]?.files[0]?.path ?? null;
}

function fileEntryOf(
  modules: readonly CodeModuleEntry[],
  path: string | null,
): { module: CodeModuleEntry; file: CodeFileEntry } | null {
  if (path === null) return null;
  for (const module of modules) {
    const file = module.files.find((f) => f.path === path);
    if (file !== undefined) return { module, file };
  }
  return null;
}

/** What a file is, in one sentence, for everything that is not a behavior handler. */
const KIND_SENTENCE: Readonly<Record<string, string>> = {
  types: 'This file lists the shapes of the things this part keeps. The machine writes it the same way every time, straight from your data model.',
  store: 'This file is how this part reads and writes its own rows. The machine writes it the same way every time.',
  config: 'This is a settings file for this part — how it is built, not what it does.',
  screen: 'This is one of your app’s screens, as the machine drew it.',
  app: 'This is part of the shell the machine built around your screens.',
};

/** The mechanical owner header for the open file, derived from the file's own bytes. */
function FileHeader({ entry, read }: { entry: CodeFileEntry; read: CodeFileRead }) {
  if (entry.kind !== 'behavior') {
    return (
      <div className="cr-head">
        <h3 className="cr-head__title">{entry.name}</h3>
        <p className="cr-head__line">{KIND_SENTENCE[entry.kind] ?? KIND_SENTENCE.app}</p>
      </div>
    );
  }

  const holes = read.holes;
  const withRules = holes.filter((h) => h.rules.length > 0);
  const title = entry.operationLabel ?? entry.name;

  return (
    <div className="cr-head">
      <h3 className="cr-head__title">This file is “{title}”.</h3>
      <p className="cr-head__line">
        The frame of it is deterministic — the machine builds it the same way every time.
        {holes.length === 0
          ? ' Nothing inside it is marked as written for this build in particular.'
          : ` ${countWord(holes.length)[0]!.toUpperCase()}${countWord(holes.length).slice(1)} ${
              holes.length === 1 ? 'function' : 'functions'
            } inside it ${holes.length === 1 ? 'was' : 'were'} written by the machine — marked ⚙ below.`}
      </p>
      {withRules.length > 0 ? (
        <>
          <p className="cr-head__line">
            {withRules.length === holes.length
              ? withRules.length === 1
                ? 'It was written to enforce one of your rules:'
                : 'They were written to enforce your rules:'
              : `${countWord(withRules.length)[0]!.toUpperCase()}${countWord(withRules.length).slice(1)} of them ${
                  withRules.length === 1 ? 'enforces a rule' : 'enforce rules'
                } of yours:`}
          </p>
          <ul className="cr-rules">
            {withRules.map((hole) =>
              hole.rules.map((rule, i) => (
                <li key={`${hole.name}-${i}`} className="cr-rules__row">
                  <span className="cr-rules__rule">{rule}</span>
                  {hole.refusals.length > 0 && (
                    <span className="cr-rules__refusal">
                      Your app refuses with: “{hole.refusals[hole.refusals.length - 1]}”
                    </span>
                  )}
                </li>
              )),
            )}
          </ul>
        </>
      ) : (
        holes.length > 0 && (
          <p className="cr-head__line">
            None of them names a rule of yours — this behavior has none to enforce.
          </p>
        )
      )}
    </div>
  );
}

/** `<pre>` with line numbers, the machine-written ranges tinted and marked in the gutter. */
function CodeViewer({ read }: { read: CodeFileRead }) {
  const lines = useMemo(() => (read.text ?? '').split('\n'), [read.text]);
  // One lookup per line rather than a scan per line: files run to thousands of lines.
  const marks = useMemo(() => {
    const inside = new Set<number>();
    const starts = new Set<number>();
    for (const hole of read.holes) {
      starts.add(hole.startLine);
      for (let n = hole.startLine; n <= hole.endLine; n += 1) inside.add(n);
    }
    return { inside, starts };
  }, [read.holes]);

  return (
    <div className="cv">
      {read.holes.length > 0 && (
        <p className="cv__legend">
          <span aria-hidden="true">⚙</span> marks a function the machine wrote. Everything
          around it is the frame — the same on every build.
        </p>
      )}
      <pre className="cv__pre" tabIndex={0}>
        {lines.map((line, i) => {
          const n = i + 1;
          const machine = marks.inside.has(n);
          return (
            <span key={n} className={`cv__line${machine ? ' cv__line--machine' : ''}`}>
              <span className="cv__ln" aria-hidden="true">
                {n}
              </span>
              <span className="cv__mark" aria-hidden="true">
                {marks.starts.has(n) ? '⚙' : ''}
              </span>
              <span className="cv__code">{line === '' ? ' ' : line}</span>
            </span>
          );
        })}
      </pre>
    </div>
  );
}

/** The right-hand pane: whatever the open file's read came to, in words when it is not text. */
function CodePane({
  entry,
  read,
  status,
  error,
}: {
  entry: { module: CodeModuleEntry; file: CodeFileEntry } | null;
  read: CodeFileRead | null;
  status: string;
  error: string | null;
}) {
  if (entry === null) {
    return <p className="page__note">Pick a part on the left to read what the machine wrote for it.</p>;
  }
  if (status === 'error') {
    return (
      <p className="form__error" role="alert">
        {error ?? 'That file could not be fetched.'}
      </p>
    );
  }
  if (read === null) {
    return (
      <p className="page__note" aria-busy="true">
        Opening {entry.file.name}…
      </p>
    );
  }
  if (read.state !== 'ready') {
    return (
      <div className="cr-head">
        <h3 className="cr-head__title">{entry.file.name}</h3>
        <p className="page__note">{read.message}</p>
      </div>
    );
  }
  return (
    <>
      <FileHeader entry={entry.file} read={read} />
      <p className="cr-head__meta">
        {entry.module.label} · {entry.file.name} · {sizeLabel(read.bytes)}
      </p>
      <CodeViewer read={read} />
    </>
  );
}

export function CodeRoom() {
  const { runId, ready, statusKey } = useRunRoom();
  const { code, cStatus, cError } = useRunCode(runId, ready, statusKey);
  const [params, setParams] = useSearchParams();

  const modules = code?.modules ?? [];
  const chosen = params.get('file');
  // The default is IMPLICIT (no URL churn on arrival); a person's own choice is written to
  // the URL, so their file survives a refresh.
  const openPath = chosen ?? defaultPath(modules);
  const entry = fileEntryOf(modules, openPath);
  const { file, fStatus, fError } = useCodeFile(runId, entry === null ? null : entry.file.path);

  // Which part's file list is expanded. It follows the open file, and a click on another
  // part's name opens that part's first file (one gesture, not two).
  const [openModule, setOpenModule] = useState<string | null>(null);
  const activeModule = openModule ?? entry?.module.slug ?? modules[0]?.slug ?? null;
  useEffect(() => {
    if (entry !== null) setOpenModule(entry.module.slug);
  }, [entry?.module.slug]);

  function select(path: string): void {
    const next = new URLSearchParams(params);
    next.set('file', path);
    // replace: Back should leave the room, not walk every file someone opened inside it.
    setParams(next, { replace: true });
  }

  const fileCount = modules.reduce((sum, m) => sum + m.files.length, 0);
  const partCount = modules.filter((m) => m.kind === 'module').length;

  return (
    <section className="sr" aria-label="Code">
      <div className="sr__head">
        <h2 className="sr__title">Code</h2>
        {cStatus === 'ready' && code?.state === 'ready' && (
          <span className="sr__meta">
            {partCount} {partCount === 1 ? 'part' : 'parts'} · {fileCount} files
          </span>
        )}
      </div>
      <p className="sr__lede">
        The machine wrote your app one part at a time — one part per module of the plan — and
        each part says, in its own text, which of your rules it enforces. This is that text,
        exactly as it was graded.
      </p>

      {cStatus === 'loading' && (
        <p className="page__note" aria-busy="true">
          Reading what the machine wrote…
        </p>
      )}
      {cStatus === 'error' && (
        <p className="form__error" role="alert">
          {cError ?? 'We couldn’t read this build’s code.'}
        </p>
      )}
      {cStatus === 'ready' && code?.state === 'absent' && (
        <p className="page__note">
          This build hasn’t written any code yet. When the machine starts on the parts of your
          app, they appear here one at a time.
        </p>
      )}

      {cStatus === 'ready' && code?.state === 'ready' && (
        <div className="cr">
          <nav className="cr__parts" aria-label="Parts of your app">
            <ul className="cr-parts">
              {modules.map((module) => {
                const open = module.slug === activeModule;
                const written = stampLabel(module.writtenAt);
                return (
                  <li key={module.slug} className={`cr-parts__part${open ? ' cr-parts__part--open' : ''}`}>
                    <button
                      className="cr-parts__name"
                      type="button"
                      aria-expanded={open}
                      onClick={() => {
                        setOpenModule(module.slug);
                        const first = module.files.find((f) => f.kind === 'behavior') ?? module.files[0];
                        if (first !== undefined) select(first.path);
                      }}
                    >
                      <span className="cr-parts__label">{module.label}</span>
                      <span className="cr-parts__count">
                        {module.kind === 'app'
                          ? `${module.files.length} files`
                          : `${module.behaviorCount} ${module.behaviorCount === 1 ? 'behavior' : 'behaviors'}`}
                        {written !== null && ` · ${written}`}
                      </span>
                    </button>
                    {open && (
                      <ul className="cr-files">
                        {module.files.map((f) => (
                          <li key={f.path}>
                            <button
                              className={`cr-files__file${f.path === openPath ? ' cr-files__file--open' : ''}`}
                              type="button"
                              aria-current={f.path === openPath ? 'true' : undefined}
                              onClick={() => select(f.path)}
                            >
                              <span className="cr-files__what">{f.operationLabel ?? f.name}</span>
                              {f.operationLabel !== null && <span className="cr-files__name">{f.name}</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="cr__view">
            <CodePane entry={entry} read={file} status={fStatus} error={fError} />
          </div>
        </div>
      )}

      <TakeItAwayLine runId={runId} />
    </section>
  );
}

// ════════════════════════════════ SCREENS ════════════════════════════════════

const CHECK_LABEL: Readonly<Record<string, string>> = {
  pass: 'Render check: passed',
  fail: 'Render check: failed',
  unchecked: 'Not checked on this build',
};

/**
 * THE LABEL, VERBATIM. It is the load-bearing sentence of this whole feature: what is in the
 * frame is a drawing the render check made with rehearsal data — no rows, blank forms, real
 * empty-state sentences — and calling it anything else would make a picture into a promise.
 * It is rendered ABOVE the frame, never inside it, so the frame's own contents can never
 * cover it. Do not reword it.
 */
const PREVIEW_LABEL =
  'Drawn by the same check that graded this screen, with rehearsal data. It is a picture, not the live app.';

/** The page put in the sandboxed frame: the app's own stylesheet, then the body fragment. */
export function composePreviewPage(html: string, css: string | null): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    `<style>${css ?? ''}</style>` +
    `</head><body>${html}</body></html>`
  );
}

/** Whatever the drawing's read came to — in a frame when there is one, in words when not. */
function PreviewPane({
  preview,
  status,
  error,
}: {
  preview: ScreenPreviewRead | null;
  status: string;
  error: string | null;
}) {
  if (status === 'error') {
    return (
      <p className="form__error" role="alert">
        {error ?? 'That picture could not be fetched.'}
      </p>
    );
  }
  if (preview === null) {
    return (
      <p className="page__note" aria-busy="true">
        Opening the picture…
      </p>
    );
  }
  if (preview.state === 'failed') {
    return (
      <p className="page__note">
        The machine could not draw this one. What it recorded instead:{' '}
        <span className="sg-preview__why">{preview.reason ?? 'no reason was recorded'}</span>
      </p>
    );
  }
  if (preview.state !== 'ready' || preview.html === null) {
    return (
      <p className="page__note">
        {preview.reason ??
          'There is no drawing of this one on this build. Start the app from your download and you are looking at the real thing.'}
      </p>
    );
  }
  return (
    <>
      <p className="sg-preview__label">{PREVIEW_LABEL}</p>
      {/* sandbox="" — the empty allow-list. No scripts, no forms, no navigation, no
          same-origin. The fragments carry no scripts; the sandbox makes that a property of
          the frame rather than something this page asks you to take on trust. */}
      <iframe
        className="sg-preview__frame"
        title={PREVIEW_LABEL}
        sandbox=""
        srcDoc={composePreviewPage(preview.html, preview.css)}
      />
    </>
  );
}

function ScreenCard({
  runId,
  screen,
  openRole,
  onOpen,
}: {
  runId: string;
  screen: ScreenDoc;
  /** Which role's drawing is open on THIS card, or null when none is. */
  openRole: string | null;
  onOpen: (role: string | null) => void;
}) {
  const seenBy = screen.visibleRoles.length > 0 ? screen.visibleRoles : screen.roles;
  const drawn = screen.previewRoles ?? [];
  const undrawn = screen.previewFailedRoles ?? [];
  const { preview, pStatus, pError } = useScreenPreview(
    runId,
    openRole === null ? null : screen.screenId,
    openRole,
  );
  return (
    <li className={`sg-card sg-card--${screen.renderCheck}${openRole !== null ? ' sg-card--open' : ''}`}>
      <p className="sg-card__title">{screen.title}</p>
      <p className="sg-card__layout">{screen.layoutLabel}</p>
      {seenBy.length > 0 ? (
        <p className="sg-card__roles">Seen by {seenBy.join(', ')}</p>
      ) : (
        <p className="sg-card__roles">Nothing on disk says who sees this one.</p>
      )}
      {screen.hiddenRoles.length > 0 && (
        <p className="sg-card__hidden">Hidden from {screen.hiddenRoles.join(', ')}</p>
      )}
      <p className={`sg-card__check sg-card__check--${screen.renderCheck}`}>
        {CHECK_LABEL[screen.renderCheck]}
        {screen.errorCodes.length > 0 && ` — ${screen.errorCodes.join(', ')}`}
      </p>

      {/* One button per role there really is a drawing for — a role with none gets no
          button, because a control that always refuses is worse than no control. */}
      {drawn.length > 0 && (
        <div className="sg-preview__roles">
          {drawn.map((role) => {
            const open = openRole === role;
            return (
              <button
                key={role}
                className={`btn ${open ? 'btn--ghost' : 'btn--quiet'} btn--sm`}
                type="button"
                aria-expanded={open}
                onClick={() => onOpen(open ? null : role)}
              >
                {open ? `Hide the picture (${role})` : `See a picture — as ${role}`}
              </button>
            );
          })}
        </div>
      )}
      {undrawn.length > 0 && (
        <p className="sg-card__undrawn">
          The machine could not draw this one for {undrawn.join(', ')} — that attempt is on
          record, and nothing is being shown in its place.
        </p>
      )}
      {openRole !== null && (
        <div className="sg-preview">
          <PreviewPane preview={preview} status={pStatus} error={pError} />
        </div>
      )}
    </li>
  );
}

/** The screens of one module, in one block. Screens nothing attributes go under their own. */
function ScreenGroup({
  runId,
  label,
  screens,
  open,
  onOpen,
}: {
  runId: string;
  label: string;
  screens: readonly ScreenDoc[];
  /** The one drawing open in the whole room, or null. */
  open: { screenId: string; role: string } | null;
  onOpen: (next: { screenId: string; role: string } | null) => void;
}) {
  return (
    <div className="sg-group">
      <h3 className="sr__section">
        {label} <span className="sr__meta">· {screens.length}</span>
      </h3>
      <ul className="sg-cards">
        {screens.map((s) => (
          <ScreenCard
            key={s.screenId}
            runId={runId}
            screen={s}
            openRole={open !== null && open.screenId === s.screenId ? open.role : null}
            onOpen={(role) => onOpen(role === null ? null : { screenId: s.screenId, role })}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * WHEN AND HOW THE DRAWINGS WERE MADE — said out loud, because the manifest records it.
 *
 * A picture drawn while the build ran and one drawn afterwards from the same files are the
 * same renderer over the same bytes, and the difference is still worth stating: it is the
 * difference between a record kept as it happened and a record reconstructed after the fact.
 * A build that recorded neither gets no sentence rather than a guessed one.
 */
function previewProvenance(previews: ScreensPreviewsDoc): string | null {
  const when = stampLabel(previews.generatedAt);
  const on = when === null ? '' : ` on ${when}`;
  if (previews.source === 'build') return `They were drawn while this build ran${on}.`;
  if (previews.source === 'backfill') {
    return `They were drawn${on}, after this build finished, from the very files it was graded on.`;
  }
  return null;
}

/**
 * DOES EVERY (screen, seen-by role) PAIR really have a drawing? The headline below claims
 * "every screen once for each role" — a universal — so it is EARNED from the per-card facts
 * rather than assumed from `drawn > 0`: no recorded failures, and every role each screen is
 * shown to appears in that screen's own drawn list. Anything less gets the partial sentence
 * with the real numbers.
 */
export function previewsCoverEveryRole(doc: ScreensDoc): boolean {
  if (doc.previews === undefined || doc.previews.failed > 0) return false;
  return doc.screens.every((s) => {
    const seenBy = s.visibleRoles.length > 0 ? s.visibleRoles : s.roles;
    const drawn = s.previewRoles ?? [];
    return seenBy.every((role) => drawn.includes(role));
  });
}

function ScreensBody({ runId, doc }: { runId: string; doc: ScreensDoc }) {
  const [role, setRole] = useState('');
  // ONE drawing open at a time, for the whole room: each carries the app's own stylesheet,
  // and a wall of frames is not a thing anyone reads.
  const [open, setOpen] = useState<{ screenId: string; role: string } | null>(null);
  const shown = useMemo(
    () =>
      role === ''
        ? doc.screens
        : doc.screens.filter((s) =>
            (s.visibleRoles.length > 0 ? s.visibleRoles : s.roles).includes(role),
          ),
    [doc.screens, role],
  );

  // Grouped by the module that owns each screen, in the order the modules first appear.
  const groups = useMemo(() => {
    const byModule = new Map<string, ScreenDoc[]>();
    for (const screen of shown) {
      const key = screen.moduleLabel ?? 'Everything else';
      const bucket = byModule.get(key);
      if (bucket === undefined) byModule.set(key, [screen]);
      else bucket.push(screen);
    }
    return [...byModule];
  }, [shown]);

  // When a role is chosen the links narrow with the screens: a path that ends on a screen
  // this person cannot open is not a path they can walk, and drawing it would say they can.
  const edges = useMemo(() => {
    const visible = new Set(shown.map((s) => s.screenId));
    const kept = role === '' ? doc.navGraph : doc.navGraph.filter((e) => visible.has(e.from) && visible.has(e.to));
    // Several screens can carry the SAME name (three different room lists all called
    // "Rooms"), and each of them links on to the same place. Printing one identical
    // sentence three times tells a reader nothing they did not already have.
    const seen = new Set<string>();
    return kept.filter((e) => {
      const sentence = `${e.fromTitle ?? e.from} ${e.toTitle ?? e.to}`;
      if (seen.has(sentence)) return false;
      seen.add(sentence);
      return true;
    });
  }, [doc.navGraph, role, shown]);

  const check = doc.check;

  return (
    <>
      {check !== null ? (
        <p className="sr__summary">
          The machine drew every screen once for each role and checked it came up clean.{' '}
          {check.failed === 0
            ? `All ${check.rendered} came up clean.`
            : `${check.rendered} came up clean; ${check.failed} did not.`}
          {check.leaks === 0 && check.hiddenRoleChecks > 0 && (
            <> It also checked {check.hiddenRoleChecks} times that a role who should not see a screen does not — and none did.</>
          )}
        </p>
      ) : (
        <p className="page__note">
          This build left no screen check on disk, so each screen below says what it is and
          nothing about whether it was drawn cleanly. Nothing is being hidden from you —
          there is genuinely no result to show.
        </p>
      )}

      {doc.roles.length > 0 && (
        <div className="sg-filter">
          <label className="sg-filter__label" htmlFor="screens-role">
            Who can see it
          </label>
          <select
            id="screens-role"
            className="sg-filter__select"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="">Anyone</option>
            {doc.roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <span className="sg-filter__count">
            {shown.length} of {doc.screens.length} screens
          </span>
        </div>
      )}

      {shown.length === 0 ? (
        <p className="page__note">No screen is shown to that role on this build.</p>
      ) : (
        groups.map(([label, screens]) => (
          <ScreenGroup
            key={label}
            runId={runId}
            label={label}
            screens={screens}
            open={open}
            onOpen={setOpen}
          />
        ))
      )}

      {edges.length > 0 && (
        <>
          <h3 className="sr__section">How they connect</h3>
          <ul className="sg-nav">
            {edges.map((edge) => (
              <li key={`${edge.from}→${edge.to}`}>
                <span className="sg-nav__from">{edge.fromTitle ?? edge.from}</span>
                <span className="sg-nav__arrow" aria-hidden="true">
                  {' → '}
                </span>
                <span className="sg-nav__to">{edge.toTitle ?? edge.to}</span>
              </li>
            ))}
          </ul>
          <p className="sr__lede">
            Those are the links the machine put in your app: standing on the first screen, the
            second is one click away.
          </p>
        </>
      )}
    </>
  );
}

export function ScreensRoom() {
  const { runId, ready, statusKey } = useRunRoom();
  const { screens, scStatus, scError } = useRunScreens(runId, ready, statusKey);
  const doc = screens?.doc ?? null;

  return (
    <section className="sr" aria-label="Screens">
      <div className="sr__head">
        <h2 className="sr__title">Screens</h2>
        {doc !== null && <span className="sr__meta">{doc.screens.length} screens</span>}
      </div>
      <p className="sr__lede">
        Your app’s screens are generated from the plan — which ones exist, who may open each,
        and how they link together all follow from the operations and the permissions you
        approved.
      </p>

      {scStatus === 'loading' && (
        <p className="page__note" aria-busy="true">
          Reading this build’s screens…
        </p>
      )}
      {scStatus === 'error' && (
        <p className="form__error" role="alert">
          {scError ?? 'We couldn’t read this build’s screens.'}
        </p>
      )}
      {scStatus === 'ready' && screens?.state === 'absent' && (
        <p className="page__note">
          This build has no screens yet — it hasn’t got that far. They are drawn near the end,
          once the machine knows every operation your app can do.
        </p>
      )}
      {scStatus === 'ready' && screens?.state === 'unintelligible' && (
        <p className="page__note">
          This build left a screen list this portal cannot read back. Rather than guess at
          what it says, this room shows nothing — the app itself is still in your download.
        </p>
      )}

      {scStatus === 'ready' && doc !== null && (
        <>
          {doc.previews !== undefined && doc.previews.drawn > 0 && previewsCoverEveryRole(doc) ? (
            // EVERY (screen, role) VIEW REALLY HAS A DRAWING — the universal sentence is
            // earned, not assumed (previewsCoverEveryRole). They are the render check's own
            // drawings, not this page's guess at someone else's app. "Kept" only when they
            // were kept: a backfilled set was made again afterwards, and says so instead.
            <p className="page__note">
              The machine drew every screen once for each role to check it, and{' '}
              {doc.previews.source === 'backfill'
                ? 'those drawings were made again afterwards from this build’s own files'
                : 'this build kept those drawings'}{' '}
              — so there are pictures here. Each one is made with rehearsal data: no records,
              blank forms, and the real sentence a list shows when it is empty.{' '}
              {previewProvenance(doc.previews)} Start the app from your download and you are
              looking at the real thing.
            </p>
          ) : doc.previews !== undefined && doc.previews.drawn > 0 ? (
            // SOME VIEWS HAVE A DRAWING AND SOME DO NOT — the honest partial: the real
            // count, the real misses, and no universal claim.
            <p className="page__note">
              There are {doc.previews.drawn} picture{doc.previews.drawn === 1 ? '' : 's'} of
              these screens here — each one drawn by the same check that graded it, with
              rehearsal data: no records, blank forms, and the real sentence a list shows when
              it is empty.
              {doc.previews.failed > 0 &&
                ` ${doc.previews.failed} view${doc.previews.failed === 1 ? '' : 's'} could not be drawn — the cards below say so where it happened, and nothing is shown in their place.`}{' '}
              {previewProvenance(doc.previews)} Start the app from your download and you are
              looking at the real thing.
            </p>
          ) : (
            // NO DRAWINGS ON THIS BUILD — the paragraph stands exactly as it was written. A
            // run with no pictures says so; it never borrows the sentence above.
            <p className="page__note">
              There are no pictures of the screens here. A drawing made on this page would be a
              drawing of this page’s guess, not of your app — so this room lists what exists and
              how each one was checked. Start the app from your download and you are looking at
              the real thing.
            </p>
          )}
          <ScreensBody runId={runId} doc={doc} />
        </>
      )}

      <TakeItAwayLine runId={runId} />
    </section>
  );
}
